<template>
<section class="pt-8 pt-md-5 pb-8 pb-md-14">
    <div class="container">
        <div class="row">
            <div class="col-12">
                <p class="mb-6 text-danger" v-for="error in errors.__all__" :key="error">{{ error }}</p>
                <Spinner v-if="step === 'loading'" />

                <div v-else-if="step === 'error'">
                    <h4 class="mb-2">Couldn't load two-factor status</h4>
                    <p class="text-muted mb-5">
                        The status request did not go through, so nothing is shown
                        or changed here until it does.
                    </p>
                    <button class="btn btn-primary" type="button" @click="load">Retry</button>
                </div>

                <div v-else-if="step === 'disabled'">
                    <h4 class="mb-2">Two-factor authentication is off</h4>
                    <p class="text-muted mb-5">
                        Add a second step to every login: a 6-digit code from an app
                        like Google Authenticator or 1Password.
                    </p>
                    <form class="mb-6" @submit.prevent="startSetup">
                        <div class="form-group mb-5">
                            <label class="form-label" for="tfa-password">Your password</label>
                            <input type="password" class="form-control" id="tfa-password"
                                   autocomplete="current-password"
                                   :class="{'is-invalid': errors.password}" v-model="password">
                            <small class="form-text text-danger" v-for="error in errors.password" :key="error">{{ error }}</small>
                        </div>
                        <button class="btn btn-primary" type="submit" :disabled="disabled || !password">
                            Enable two-factor
                        </button>
                    </form>
                </div>

                <div v-else-if="step === 'qr'">
                    <h4 class="mb-2">Scan this code</h4>
                    <p class="text-muted mb-4">Open your authenticator app and scan the image, then enter the code it shows.</p>
                    <img v-if="qr" :src="qr" alt="TOTP QR code" class="mb-4" width="200" height="200">
                    <p class="mb-4">
                        Can't scan? Enter this key manually:<br>
                        <code class="fs-5">{{ secret }}</code>
                        <button class="btn btn-sm btn-outline-secondary ms-2" type="button" @click="copy(secret)">Copy</button>
                    </p>
                    <form class="mb-6" @submit.prevent="enable">
                        <div class="form-group mb-5">
                            <label class="form-label" for="tfa-code">Code from the app</label>
                            <input type="text" class="form-control" id="tfa-code" inputmode="numeric"
                                   autocomplete="one-time-code" placeholder="123456" maxlength="32" ref="firstField"
                                   :class="{'is-invalid': errors.code}" v-model="code">
                            <small class="form-text text-danger" v-for="error in errors.code" :key="error">{{ error }}</small>
                        </div>
                        <button class="btn btn-primary" type="submit" :disabled="disabled || !code.trim()">Confirm</button>
                        <button class="btn btn-link" type="button" @click="load">Cancel</button>
                    </form>
                </div>

                <div v-else-if="step === 'recovery'">
                    <h4 class="mb-2">Save your recovery codes</h4>
                    <p class="text-danger mb-4">
                        Shown once. Each code logs you in exactly once if you lose your phone.
                    </p>
                    <pre class="mb-4 fs-5">{{ recoveryCodes.join('\n') }}</pre>
                    <button class="btn btn-outline-secondary me-2" type="button" @click="copy(recoveryCodes.join('\n'))">Copy all</button>
                    <div class="form-check my-4">
                        <input class="form-check-input" type="checkbox" id="tfa-saved" v-model="acknowledged">
                        <label class="form-check-label" for="tfa-saved">
                            I saved them somewhere safe
                        </label>
                    </div>
                    <button class="btn btn-primary" type="button" :disabled="!acknowledged" @click="load">Done</button>
                </div>

                <div v-else-if="step === 'regenerate'">
                    <h4 class="mb-2">Regenerate recovery codes</h4>
                    <p class="text-muted mb-5">
                        The 8 codes you have now stop working. Confirm with your
                        password and a code from your authenticator app.
                    </p>
                    <form class="mb-6" @submit.prevent="regenerate">
                        <div class="form-group mb-5">
                            <label class="form-label" for="tfa-regenerate-password">Your password</label>
                            <input type="password" class="form-control" id="tfa-regenerate-password"
                                   autocomplete="current-password" ref="firstField"
                                   :class="{'is-invalid': errors.password}" v-model="password">
                            <small class="form-text text-danger" v-for="error in errors.password" :key="error">{{ error }}</small>
                        </div>
                        <div class="form-group mb-5">
                            <label class="form-label" for="tfa-regenerate-code">Current code</label>
                            <input type="text" class="form-control" id="tfa-regenerate-code" inputmode="numeric"
                                   autocomplete="one-time-code" placeholder="123456 or a recovery code" maxlength="32"
                                   :class="{'is-invalid': errors.code}" v-model="code">
                            <small class="form-text text-danger" v-for="error in errors.code" :key="error">{{ error }}</small>
                        </div>
                        <button class="btn btn-primary me-2" type="submit" :disabled="disabled || !password || !code.trim()">
                            Regenerate
                        </button>
                        <button class="btn btn-link" type="button" @click="load">Cancel</button>
                    </form>
                </div>

                <div v-else-if="step === 'disable'">
                    <h4 class="mb-2 text-danger">Turn off two-factor</h4>
                    <p class="text-muted mb-5">
                        Your account goes back to a single password step. Confirm with
                        your password and a code from your authenticator app.
                    </p>
                    <form class="mb-6" @submit.prevent="disable">
                        <div class="form-group mb-5">
                            <label class="form-label" for="tfa-password-off">Your password</label>
                            <input type="password" class="form-control" id="tfa-password-off"
                                   autocomplete="current-password" ref="firstField"
                                   :class="{'is-invalid': errors.password}" v-model="password">
                            <small class="form-text text-danger" v-for="error in errors.password" :key="error">{{ error }}</small>
                        </div>
                        <div class="form-group mb-5">
                            <label class="form-label" for="tfa-disable-code">Current code</label>
                            <input type="text" class="form-control" id="tfa-disable-code" inputmode="numeric"
                                   autocomplete="one-time-code" placeholder="123456 or a recovery code" maxlength="32"
                                   :class="{'is-invalid': errors.code}" v-model="code">
                            <small class="form-text text-danger" v-for="error in errors.code" :key="error">{{ error }}</small>
                        </div>
                        <button class="btn btn-danger me-2" type="submit" :disabled="disabled || !password || !code.trim()">
                            Turn off two-factor
                        </button>
                        <button class="btn btn-link" type="button" @click="load">Cancel</button>
                    </form>
                </div>

                <div v-else>
                    <h4 class="mb-2">Two-factor authentication is on</h4>
                    <p class="text-muted mb-2" v-if="status.confirmed_at">Enabled on {{ status.confirmed_at.slice(0, 10) }}</p>
                    <p class="text-muted mb-5">{{ status.recovery_codes_left }} recovery codes left</p>
                    <button class="btn btn-outline-primary me-2" type="button" @click="goto('regenerate')">
                        Regenerate recovery codes
                    </button>
                    <button class="btn btn-outline-danger" type="button" @click="goto('disable')">
                        Turn off two-factor
                    </button>
                </div>
            </div>
        </div>
    </div>
</section>
</template>

<script>
import QRCode from "qrcode"
import { NotifyMixin } from '@/mixins'
import { displayErrors } from "utils/string"
import Spinner from "../Spinner.vue"

export default {
    mixins: [NotifyMixin],
    components: { Spinner },
    data() {
        return {
            // loading | error | disabled | qr | recovery | enabled | regenerate | disable
            step: 'loading',
            status: {},
            secret: '',
            qr: '',
            code: '',
            password: '',
            recoveryCodes: [],
            acknowledged: false,
            errors: {},
            disabled: false
        }
    },
    created() {
        this.load()
    },
    methods: {
        reset() {
            this.errors = {}
            this.code = ''
            this.password = ''
            this.recoveryCodes = []
            this.acknowledged = false
            // A cancelled setup is revoked server-side (setup uses force=True),
            // so the old secret and its QR must never survive into a new one.
            this.secret = ''
            this.qr = ''
        },
        load() {
            this.reset()
            this.step = 'loading'
            // GET /api/users/tfa answers TFAStatusResponse, which returns
            // `state: true` plus `enabled` — branch on `enabled`.
            this.$services.Tfa().get().then(resp => {
                this.status = resp.body
                this.step = resp.body.enabled ? 'enabled' : 'disabled'
            }).catch(resp => {
                // Never claim "two-factor is off" for an account whose status we
                // could not read (500/503/401) — that offers an enable form the
                // backend would reject and misinforms a security panel.
                displayErrors(resp, this)
                this.step = 'error'
            })
        },
        goto(step) {
            this.reset()
            this.step = step
            this.focusFirstField()
        },
        // Moves focus to the first input of whichever step is now on screen.
        // qr/regenerate/disable all render at most one input with
        // ref="firstField" at a time (mutually exclusive v-else-if branches),
        // so a single ref name is enough to reach the right element.
        focusFirstField() {
            this.$nextTick(() => {
                if (this.$refs.firstField) this.$refs.firstField.focus()
            })
        },
        startSetup() {
            if (!this.password) return
            this.errors = {}
            this.disabled = true
            this.$services.TfaSetup().save({ password: this.password }).then(resp => {
                if (!resp.body.state) return displayErrors(resp, this)
                this.secret = resp.body.secret
                // Blank first: `step = 'qr'` paints synchronously, toDataURL
                // resolves later, so a leftover image would show the previous
                // (already revoked) secret next to the new one.
                this.qr = ''
                this.password = ''
                this.code = ''
                this.step = 'qr'
                this.focusFirstField()
                return QRCode.toDataURL(resp.body.otpauth_uri).then(url => {
                    this.qr = url
                }).catch(() => {
                    // Not an HTTP response — displayErrors would print its
                    // "Request failed" fallback over a working setup.
                    this.errors = {'__all__': ["Could not draw the QR code — enter the key below manually"]}
                })
            }).catch(resp => {
                displayErrors(resp, this)
            }).finally(() => {
                this.disabled = false
            })
        },
        // A wrong code comes back as 403 with a string `detail`; displayErrors
        // puts that into errors.__all__ (top of the tab). Show it under the
        // code field instead — 403 is the only status the code endpoints use
        // for a rejected code.
        showCodeError(resp) {
            displayErrors(resp, this)
            if (resp?.status === 403 && this.errors.__all__) {
                this.errors = { code: this.errors.__all__ }
            }
        },
        enable() {
            if (!this.code.trim()) return
            this.errors = {}
            this.disabled = true
            this.$services.TfaEnable().save({ code: this.code.trim() }).then(resp => {
                if (!resp.body.state) return displayErrors(resp, this)
                this.recoveryCodes = resp.body.recovery_codes
                this.acknowledged = false
                this.code = ''
                this.step = 'recovery'
            }).catch(resp => {
                this.showCodeError(resp)
            }).finally(() => {
                this.disabled = false
            })
        },
        disable() {
            if (!this.password || !this.code.trim()) return
            this.errors = {}
            this.disabled = true
            this.$services.TfaDisable().save({ password: this.password, code: this.code.trim() }).then(resp => {
                if (!resp.body.state) return displayErrors(resp, this)
                this.successNotify({ message: "Two-factor authentication turned off" })
                this.load()
            }).catch(resp => {
                this.showCodeError(resp)
            }).finally(() => {
                this.disabled = false
            })
        },
        // A fresh set kills the old one, so this is the same step-up as
        // disable: password + code, not a code on its own.
        regenerate() {
            if (!this.password || !this.code.trim()) return
            this.errors = {}
            this.disabled = true
            this.$services.TfaRecoveryCodes().save({ password: this.password, code: this.code.trim() }).then(resp => {
                if (!resp.body.state) return displayErrors(resp, this)
                this.recoveryCodes = resp.body.recovery_codes
                this.acknowledged = false
                this.code = ''
                this.password = ''
                this.step = 'recovery'
            }).catch(resp => {
                this.showCodeError(resp)
            }).finally(() => {
                this.disabled = false
            })
        },
        copy(text) {
            // $methods.copyToClipboard falls back to execCommand outside a
            // secure context — navigator.clipboard is undefined on plain http.
            this.$methods.copyToClipboard(text)
            this.successNotify({ message: "Copied" })
        }
    }
}
</script>
